import type { ApplySupplierImportLine, SupplierImportCandidateDto, SupplierImportDto } from '@/lib/api/v2/planning'
import { sameBrand } from '@/lib/supplierDirectory'

/**
 * SOLY-SUPPLIER-002 MS-03 — the screen state of an import's proposals, and what is sent back. The
 * person only removes what is wrong: sure lines start ticked, unsure ones unticked; a line keeps its
 * name, target and brands as edited until the decision succeeds.
 */
export interface ImportLineState {
  checked: boolean
  name: string
  /** null = « Nouveau fournisseur ». Always null for a platform. */
  targetSupplierId: string | null
  brands: string[]
}

export type ImportLines = Record<string, ImportLineState>

export function initialLines(candidates: SupplierImportCandidateDto[]): ImportLines {
  return Object.fromEntries(
    candidates.map((c) => [
      c.id,
      {
        checked: c.level === 'Sure',
        name: c.suggestedName ?? c.domain.slice(1),
        targetSupplierId: c.kind === 'Platform' ? null : c.targetSupplierId,
        brands: c.kind === 'Platform' ? [] : [...c.suggestedBrands],
      },
    ]),
  )
}

export function toApplyLines(candidates: SupplierImportCandidateDto[], lines: ImportLines): ApplySupplierImportLine[] {
  return candidates.map((c) => {
    const l = lines[c.id]
    return {
      candidateId: c.id,
      checked: l?.checked ?? false,
      name: l?.name.trim() || null,
      targetSupplierId: c.kind === 'Platform' ? null : (l?.targetSupplierId ?? null),
      brands: c.kind === 'Platform' ? [] : (l?.brands ?? []),
    }
  })
}

// The person's choices on an import's proposals, kept while the page lives: closing the panel with
// « Plus tard » and reopening it finds every tick, name, target and brand as left.
const drafts = new Map<string, ImportLines>()

export function draftLines(runId: string, candidates: SupplierImportCandidateDto[]): ImportLines {
  const saved = drafts.get(runId)
  return saved && candidates.every((c) => saved[c.id]) ? saved : initialLines(candidates)
}

export function keepDraft(runId: string, lines: ImportLines) {
  drafts.set(runId, lines)
}

export function forgetDraft(runId: string) {
  drafts.delete(runId)
}

export const checkedCount = (lines: ImportLines) => Object.values(lines).filter((l) => l.checked).length

/** « Ajouter les 7 lignes cochées » — or « Tout ignorer » when nothing is ticked. */
export function applyLabel(count: number): string {
  if (count === 0) return 'Tout ignorer'
  return count === 1 ? 'Ajouter la ligne cochée' : `Ajouter les ${count} lignes cochées`
}

/** Adds a brand unless the line already has it under another spelling (« quooker » = « Quooker »). */
export function withBrand(brands: string[], raw: string): string[] {
  const brand = raw.trim().replace(/\s+/g, ' ')
  if (!brand || brands.some((b) => sameBrand(b, brand))) return brands
  return [...brands, brand]
}

/** « secretariat@kitchy.be » → « secretariat@ » : the launch sentence names mailboxes the way people say them. */
export const shortMailbox = (address: string) => address.slice(0, address.indexOf('@') + 1)

export function launchSentence(mailboxes: string[]): string {
  const list = mailboxes.map(shortMailbox).join(', ')
  return (
    `Soly lit les objets des emails envoyés et reçus des boîtes suivies pour les commandes (${list}), avec des sociétés. ` +
    'Une IA reconnaît vos fournisseurs. Aucun email n’est ouvert. Vos clients, vos échanges internes et les adresses ' +
    'Gmail, Skynet ou Telenet sont écartés avant.'
  )
}

export const isRunning = (dto: SupplierImportDto | null) => dto?.status === 'Reading' || dto?.status === 'Classifying'

/** Survives the Microsoft round trip: set when the import was asked before any mailbox was connected. */
export const PENDING_IMPORT_KEY = 'soly.supplierImport.pendingAfterMailbox'

export function rememberPendingImport() {
  try {
    sessionStorage.setItem(PENDING_IMPORT_KEY, '1')
  } catch {
    // Storage blocked: the person relaunches by hand after adding a mailbox.
  }
}

/** True once: the flag is cleared when read. */
export function takePendingImport(): boolean {
  try {
    const pending = sessionStorage.getItem(PENDING_IMPORT_KEY) === '1'
    sessionStorage.removeItem(PENDING_IMPORT_KEY)
    return pending
  } catch {
    return false
  }
}
